import { DateTime } from 'luxon';
import { Link } from 'react-router-dom-v5-compat';

import AttributeItem from 'dashboard/components/AttributeItem';
import VerificationStatusSticker from 'dashboard/components/VerificationStatusSticker';
import { InfoGrid } from 'dashboard/layouts/ShowViewLayoutV2';
import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { formatTimeRangeDuration } from 'dashboard/lib/utils/formatters';
import { Verification } from 'dashboard/models/verification';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';

export interface VerificationInfoProps {
  verification: Verification;
}

const VerificationInfo = ({ verification }: VerificationInfoProps) => {
  const { 'created-at': createdAt, 'completed-at': completedAt } = verification.attributes;
  const createdAtFormatted = DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_TZ_FORMAT);
  const completedAtFormatted = completedAt
    ? DateTime.fromISO(completedAt).toFormat(DATE_AND_TIME_TZ_FORMAT)
    : undefined;

  const accountId = verification.relationships['account']?.data?.id;
  const inquiryId = verification.relationships['inquiry']?.data?.id;
  const transactionId = verification.relationships['transaction']?.data?.id;
  const verificationTemplateId = verification.relationships['verification-template']?.data?.id;
  const verificationTemplateVersionId =
    verification.relationships['verification-template-version']?.data?.id;

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>Info</Card.Title>
          <InfoGrid>
            <AttributeItem
              title='Verification ID'
              value={verification.id}
              isRedacted={false}
              placeholder=''
              wordBreak='break-all'
              dataTest='verification-details-info__verification-id'
            />
            <AttributeItem
              title='Type'
              value={VERIFICATION_TYPE_NAMES_SHORT[verification.type]}
              isRedacted={false}
              placeholder=''
              wordBreak='break-all'
              dataTest='verification-details-info__verification-type'
            />
            <AttributeItem
              title='Status'
              value={<VerificationStatusSticker verification={verification} />}
              placeholder=''
              wordBreak='break-all'
              dataTest='verification-details-info__status'
            />
            <AttributeItem
              title='Account ID'
              value={
                accountId != null ? (
                  <Link to={`/dashboard/accounts/${accountId}`}>{accountId}</Link>
                ) : null
              }
              isRedacted={false}
              placeholder='No account ID'
              wordBreak='break-all'
              dataTest='verification-details-info__account-id'
            />
            <AttributeItem
              title='Inquiry ID'
              value={
                inquiryId != null ? (
                  <Link to={`/dashboard/inquiries/${inquiryId}`}>{inquiryId}</Link>
                ) : null
              }
              isRedacted={false}
              placeholder='No inquiry ID'
              wordBreak='break-all'
              dataTest='verification-details-info__inquiry-id'
            />
            <AttributeItem
              title='Transaction ID'
              value={
                transactionId != null ? (
                  <Link to={`/dashboard/transactions/${transactionId}`}>{transactionId}</Link>
                ) : null
              }
              isRedacted={false}
              placeholder='No transaction ID'
              wordBreak='break-all'
              dataTest='verification-details-info__transaction-id'
            />
            <AttributeItem
              title='Verification Template Version'
              value={
                verificationTemplateId != null ? (
                  <Link
                    to={`/dashboard/verification-templates/${verificationTemplateId}/${verificationTemplateVersionId}`}
                  >
                    {verificationTemplateVersionId}
                  </Link>
                ) : null
              }
              isRedacted={false}
              placeholder=''
              wordBreak='break-all'
              dataTest='verification-details-info__verification-template-id'
            />
            <AttributeItem
              title='Created At'
              value={createdAtFormatted}
              isRedacted={false}
              placeholder='Unknown creation date'
              wordBreak='break-all'
              dataTest='verification-details-info__created-at'
            />
            {completedAt != null && (
              <>
                <AttributeItem
                  title='Completed At'
                  value={completedAtFormatted}
                  isRedacted={false}
                  placeholder='Unknown completion date'
                  wordBreak='break-all'
                  dataTest='verification-details-info__completed-at'
                />
                <AttributeItem
                  title='Duration'
                  value={formatTimeRangeDuration(createdAt, completedAt).completionTime}
                  isRedacted={false}
                  placeholder=''
                  wordBreak='break-all'
                  dataTest='verification-details-info__duration'
                />
              </>
            )}
          </InfoGrid>
        </Card.Body>
      </Card>
    </>
  );
};

export default VerificationInfo;
